<template>
  <div>
    <h1 class="mb-2 font-medium text-left text-gray-900">
      {{ $t("device.form.alarmDestinations") }}
    </h1>

    <dd class="mt-1 text-sm text-gray-900">
      <ul class="divide-y divide-gray-200">
        <li class="flex items-center justify-between pt-2 pb-4 pr-4 text-sm divide-gray-950" v-for="(destination, index) in controllersIds" :key="(destination, index)">

          <div v-if="destination.scaip" class="relative items-center flex-1 w-0 gap-2 lg:flex lg:flex-wrap">
            <PhoneOutgoingIcon v-if="destination.direction == 'outbound'" class="w-full h-5 mx-auto mb-2 ml-2 text-gray-400 lg:w-5 lg:mb-0 lg:ml-0" />
            <PhoneIncomingIcon v-else class="w-full h-5 mx-auto mb-2 ml-2 text-gray-400 lg:w-5 lg:mb-0 lg:ml-0" />
            <div class="flex flex-col items-stretch flex-1 gap-2 ml-2 text-sm" @keyup="validateControllerIdAt(index)" @change="validateControllerIdAt(index)">
              <span class="font-mono text-center break-all lg:text-left">{{ destination.call ? destination.call.callee : '' }}</span>
              <input :disabled="isEditable(controllersIds, index)" v-model="destination.controllerId" @input="onControllerIdInput($event.target.value)" :placeholder="$t('device.header.id')" type="text" :class="[
                'w-full h-8 border-gray-300 rounded-md shadow-sm font-mono flex-1',
                destination.$error !== '' && index == 0 && destination.flow == 'scaip'
                  ? 'border-red-500 focus:border-red-600 focus:ring-red-500'
                  : 'focus:ring-indigo-500 focus:border-indigo-500',
                isEditable(controllersIds, index) ? 'cursor-not-allowed text-gray-400 border-unset' : 'cursor-text']" />
            </div>
            <div class="mt-2 font-mono text-sm text-center text-gray-500 lg:mt-0 lg:ml-5 lg:text-right whitespace-nowrap">
              {{ destination.attempts }} Versuche
            </div>
          </div>

          <div v-else class="flex flex-wrap items-center flex-1 w-0 gap-2">
            <PhoneOutgoingIcon v-if="destination.direction == 'outbound'" class="w-5 h-5 text-gray-400" />
            <PhoneIncomingIcon v-else class="w-5 h-5 text-gray-400" />
            <span class="items-stretch flex-1 ml-2 font-mono text-sm">{{ destination.call ? destination.call.callee : '' }}</span>
            <div class="font-mono text-sm text-right text-gray-500 whitespace-nowrap">{{ destination.attempts }} Versuche</div>
          </div>
        </li>
      </ul>
      <div class="h-6 pb-3 pl-1 mt-1 mb-2">
        <div class="flex items-center text-sm text-red-500" v-if="controllersIds.filter((controllerId) => controllerId.$error !== '' && controllerId.flow === 'scaip').length">
          <ExclamationCircleIcon class="w-5 h-5" aria-hidden="true" />
          <p class="ml-4">{{controllersIds.find((controllerId) => controllerId.$error)?.$error}}</p>
        </div>
      </div>
    </dd>

  </div>
</template>

<script>
import { computed, reactive, watch } from "vue";
import { useStore } from "vuex";
import { ExclamationCircleIcon, PhoneIncomingIcon, PhoneOutgoingIcon } from "@heroicons/vue/outline";
import i18n from "@/i18n";

export default {
  name: "AlarmDestinationsSettings",
  components: {
    ExclamationCircleIcon,
    PhoneIncomingIcon,
    PhoneOutgoingIcon,
  },

  props: ["refresh"],
  emits: ["updateConfig"],

  setup(props, { emit }) {
    const store = useStore();

    const device = computed(() => store.state.device.current);
    const alarmDestinations = computed(() => device.value.configV2.alarm_app.destinations);

    const isValidControllerId = (controllerId) => {
      return /^[A-Z0-9-]+$/.test(controllerId);
    };

    const controllersIds = reactive(
      device.value?.configV2?.alarm_app?.destinations?.map((item) => ({
        $error: item.scaip && isValidControllerId(item.scaip.controller_id) ? "" : item.$error ? item.$error : i18n.global.t("scaip.form.errorControllerId"),
        ...item,
        controllerId: item.scaip ? item.scaip.controller_id : "",
        flow: item.flow ? item.flow : "",
      }))
    );

    const validateControllerIdAt = (index) => {
      const controller_id = controllersIds[index].controllerId;
      if (!isValidControllerId(controller_id)) {
        controllersIds[index].$error = i18n.global.t("scaip.form.errorControllerId");
      } else {
        controllersIds[index].$error = "";
      }
      updateControllersIds();
    };

    const updateControllersIds = () => {
      emit(
        "updateConfig",
        controllersIds.map((controllerId, index) => ({ index: index, value: controllerId.controllerId, valid: controllerId.$error !== "" ? false : true, flow: controllerId.flow })))
    };


    watch(() => props.refresh, () => {
      if (props.refresh) {
        reset()
      }
    })

    watch(device, () => {
      if (device.value)
        reset()
    })

    const reset = () => {
      const fresh = device.value.configV2.alarm_app.destinations.map((item) => ({
        $error: item.scaip && isValidControllerId(item.scaip.controller_id) ? "" : item.$error ? item.$error : i18n.global.t("scaip.form.errorControllerId"),
        ...item,
        controllerId: item.scaip ? item.scaip.controller_id : "",
        flow: item.flow ? item.flow : "",
      }));
      controllersIds.splice(0, controllersIds.length, ...fresh);
    }

    const onControllerIdInput = (value) => {
      controllersIds.forEach((dest) => {
        if (dest.flow == "scaip") {
          dest.controllerId = value;
          dest.$error = isValidControllerId(value) ? "" : i18n.global.t("scaip.form.errorControllerId");
        }
      });
      updateControllersIds();
    };

    const isEditable = (controllerIds, index) => {
      const current = controllerIds[index];
      if (current.flow !== "scaip") return false;

      const firstIndex = controllerIds.findIndex(
        (item) => item.flow === "scaip" && item.controllerId === current.controllerId
      );
      return index != firstIndex;
    };

    return {
      device,
      alarmDestinations,
      isValidControllerId,
      validateControllerIdAt,
      controllersIds,
      onControllerIdInput,
      isEditable
    };
  },
};
</script>
